import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { PageTabs } from '../../components/PageTabs';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import HeaderInfo from '../../components/HeaderInfo';
import Notification from '../../components/Notification';
import SingleSuspendPopup from '../../components/SingleSuspendPopup';
import MultipleSuspendPopup from '../../components/MultipleSuspendPopup';

import '../../assets/css/Reports.css'
import '../../assets/css/Popup.css'
import OpenReports from './OpenReports';
import CloseReport from './CloseReports';
import EditReport from '../../components/Reports/EditReport';
import { getReportCounts, reportListApi, suspendReportApi, suspendUserApi } from '../../api/usersApi';

const Reports = () => {

    const location = useLocation();
    const userEditRef = useRef();
    const suspendVcRef = useRef();
    const suspendMultipleVcRef = useRef();
    const userTypeSelectRef = useRef();
    const reasonRef = useRef();
    const debounceRef = useRef(null);

    const [reportList, setReportList] = useState([]);
    const [totalPage, setTotalPage] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [reportCount, setReportCount] = useState([])
    const [activePage, setActivePage] = useState(`Open Reports (${reportCount?.OPEN || 0})`);
    const [ProfileTabs, setProfileTabs] = useState([]);
    const [menuActive, setMenuActive] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [isEditReport, setEditReport] = useState(false);
    const [selectedReport, setSelectedReport] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [toggleSelect, setToggleSelect] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [reportSuspendPopup, setReportSuspendPopup] = useState(false);
    const [multipleReportSuspendPopup, setMultipleReportSuspendPopup] = useState(false);
    const [reason, setReason] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isSuspendLoading, setIsSuspendLoading] = useState(false);
    const [isIgnoreLoading, setIsIgnoreLoading] = useState(false);
    const [filterReport, setFilterReport] = useState({
        userType: [],
        reasons: [],
        status: [],
    });

    const closeReportCount = (reportCount?.IGNORED) + (reportCount?.SUSPENDED) + (reportCount?.PENDING);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userEditRef?.current && !userEditRef?.current.contains(event.target)) {
                setEditReport(false);
            }
            if (suspendVcRef?.current && !suspendVcRef?.current.contains(event.target)) {
                setReportSuspendPopup(false);
            }
            if (suspendMultipleVcRef?.current && !suspendMultipleVcRef?.current.contains(event.target)) {
                setMultipleReportSuspendPopup(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const currentReportStatus = useMemo(() => {
        const path = location.pathname.toLowerCase();
        if (path.includes('/openreports')) return 'OPEN';
        if (path.includes('/closereports')) return 'CLOSE';
        return 'OPEN';
    }, [location.pathname]);

    useEffect(() => {
        const statusLabelMap = {
            OPEN: `Open Reports (${reportCount?.OPEN || 0})`,
            CLOSE: `Closed Reports (${closeReportCount || 0})`,
        };
        setActivePage(statusLabelMap[currentReportStatus]);
        setProfileTabs([
            { name: statusLabelMap['OPEN'], href: '/reports/openreports' },
            { name: statusLabelMap['CLOSE'], href: '/reports/closereports' },
        ]);
        setSelectedRows([]);
        setIsAllSelected?.(false);
    }, [currentReportStatus, reportCount, closeReportCount]);

    useEffect(() => {
        setIsLoading(true);
        setIsLoadingData(true);

        if (debounceRef?.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            if (currentReportStatus === "OPEN") {
                fetchOpenReportList();
            } else if (currentReportStatus === "CLOSE") {
                fetchCloseReportList();
            }
        }, 1500);

        return () => clearTimeout(debounceRef.current);
    }, [currentPage, searchQuery, location?.pathname, filterReport, currentReportStatus]);

    useEffect(() => {
        fetchReportsCount();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterReport])

    const fetchReportsCount = async () => {
        try {
            const res = await getReportCounts();
            const reportObj = res?.reduce((acc, item) => {
                acc[item?.status] = item?.count;
                return acc;
            }, {});

            setReportCount(reportObj);
        } catch (error) {
            console.log(error);
        }
    };

    const fetchReportList = async (status) => {
        const data = await reportListApi({
            status,
            page: currentPage || 1,
            search: searchQuery?.toLowerCase() || undefined,
            userType: filterReport?.userType?.map((item) => item?.toString()),
            reasons: filterReport?.reasons?.map((item) => item?.toString()),
        }, setIsLoadingData);
        return data;
    }

    const fetchCloseReportList = async () => {
        setIsLoading(false);
        try {
            setIsLoading(true);
            setIsLoadingData(true);
            const reportList = async () => {
                const statuses = filterReport?.status
                    ? filterReport?.status
                    : ["CLOSED"];

                const promises = statuses?.map(async (status) => {
                    const data = await fetchReportList(status);
                    return {
                        list: data?.list,
                        total: data?.total,
                    };
                });

                const results = await Promise.all(promises);
                if (results) {
                    const combinedList = results?.flatMap(res => res?.list);
                    const combinedTotal = results?.reduce((sum, res) => sum + res?.total, 0);
                    setReportList(combinedList);
                    setTotalPage(Math.ceil(combinedTotal / 10));
                    setIsLoading(false);
                }
            }
            if (filterReport?.status?.length === 1) {

                if (debounceRef?.current) {
                    clearTimeout(debounceRef.current);
                }

                debounceRef.current = setTimeout(() => {
                    reportList();
                }, 1500);

                return () => clearTimeout(debounceRef.current);
            } else {
                const data = await fetchReportList("CLOSED");
                if (data?.list?.length <= 0) {
                    setIsLoadingData(false)
                }
                setReportList(data?.list);
                setTotalPage(data?.totalPage);
                setIsLoading(false);
            }

        } catch (error) {
            console.error(error);
            setIsLoading(false);
            setIsLoadingData(false);
        }
    };

    const fetchOpenReportList = async () => {
        setIsLoading(false);
        try {
            setIsLoading(true);
            setIsLoadingData(true);
            const data = await fetchReportList("OPEN");
            if (data?.list?.length <= 0) {
                setIsLoadingData(false)
            }
            setReportList(data?.list);
            setTotalPage(data?.totalPage);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setIsLoading(false);
            setIsLoadingData(false);
        }
    };

    const actionReport = async (reportId, status) => {
        try {
            if (selectedRows?.length > 1) {
                for (const reportId of selectedRows) {
                    await suspendReportApi({
                        reportId,
                        status,
                    });
                }
            } else {
                await suspendReportApi({
                    reportId,
                    status,
                });
            }
            return true;
        } catch (error) {
            console.error('API error:', error.response?.data || error.message);
            return false;
        }
    };

    const handleSuspendUser = async (editSliderData) => {
        setIsSuspendLoading(false);
        setShowNotification(false);
        try {
            setIsSuspendLoading(true)
            const success = await actionReport(editSliderData?.id, "SUSPENDED");
            if (success) {
                const data = await fetchReportList("OPEN")
                if (data) {
                    await fetchReportsCount();
                    setIsLoadingData(false);
                    setReportList(data?.list);
                    setTotalPage(data?.totalPage);
                    setShowNotification(true);
                    setNotificationMessage(`${editSliderData?.reportedName || ""} was suspended and the report has been successfully closed.` || "");
                    setTimeout(() => {
                        setShowNotification(false);
                    }, 8000);
                    setIsSuspendLoading(false);
                    setEditReport(false);
                    if (data?.list?.length === 0) {
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1);
                    }
                }
                return true
            } else {
                setIsSuspendLoading(false);
            }
        } catch (error) {
            console.log(error);
            setIsSuspendLoading(false);
        }
    }

    const handleReportSuspend = async () => {
        let notificationText = '';
        setIsSuspendLoading(false);
        if (!reason.trim()) {
            setErrorMessage("Please enter reason for suspension.");
            return;
        }
        setIsSuspendLoading(true);
        if (reportSuspendPopup) {
            notificationText = `${selectedReport?.reportedName} was suspended and the report has been successfully closed.`;
        } else {
            notificationText = `${selectedRows?.length} users were suspended and the report has been successfully closed.`;
        }
        const success = await actionReport(selectedReport?.id, "SUSPENDED");
        if (success) {
            const data = await fetchReportList("OPEN");
            await fetchReportsCount();
            if (data) {
                setReportList(data?.list);
                setTotalPage(data?.totalPage);
                setShowNotification(true);
                setNotificationMessage(notificationText);
                setToggleSelect(!toggleSelect);
                setTimeout(() => {
                    setShowNotification(false);
                }, 8000);
                setMultipleReportSuspendPopup(false);
                setReportSuspendPopup(false);
                setReason('');
                setSelectedRows([]);
                if (data?.list?.length === 0) {
                    setCurrentPage(currentPage > 1 ? currentPage - 1 : 1);
                }
            }
            setIsSuspendLoading(false);
        } else {
            setIsSuspendLoading(false);
        }
    };

    const handleIgnoreUser = async (editSliderData) => {
        setIsIgnoreLoading(false);
        setShowNotification(false);
        try {
            setIsIgnoreLoading(true)
            const success = await actionReport(editSliderData?.id, "IGNORED");
            if (success) {
                const data = await fetchReportList("OPEN")
                if (data) {
                    await fetchReportsCount();
                    setReportList(data?.list);
                    setTotalPage(data?.totalPage);
                    setShowNotification(true);
                    setNotificationMessage('The report has been successfully ignored & closed.' || "");
                    setTimeout(() => {
                        setShowNotification(false);
                    }, 8000);
                    setIsIgnoreLoading(false);
                    setEditReport(false);
                    if (data?.list?.length === 0) {
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1);
                    }
                }
                return true
            } else {
                setIsIgnoreLoading(false);
            }
        } catch (error) {
            console.log(error);
            setIsIgnoreLoading(false);
        }
    }

    const ReportUserList = useMemo(() => {
        return reportList
    }, [reportList])

    const LazyImage = ({ src, alt }) => {
        const [isVisible, setIsVisible] = useState(false);
        const imgRef = useRef();

        useEffect(() => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.disconnect();
                    }
                },
                { threshold: 0.1 }
            );

            if (imgRef.current) {
                observer.observe(imgRef.current);
            }

            return () => {
                if (imgRef.current) {
                    observer.unobserve(imgRef.current);
                }
            };
        }, []);

        return (
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                loading="lazy"
            />
        );
    };

    const reportProps = {
        setEditReport,
        reportList: ReportUserList,
        setSelectedReport,
        totalPage,
        setCurrentPage,
        currentPage,
        searchQuery,
        setSearchQuery,
        isAllSelected,
        setIsAllSelected,
        selectedRows,
        setSelectedRows,
        isLoadingData,
        setIsLoadingData,
        isLoading,
        toggleSelect,
        setReportSuspendPopup,
        setMultipleReportSuspendPopup,
        suspendMultipleVcRef,
        userTypeSelectRef,
        reasonRef,
        LazyImage,
        setReason,
        filterReport,
        setFilterReport,
        setErrorMessage
    }
    const getReportComponent = (Component) => <Component {...reportProps} />;

    return (
        <div className='main-wrapper'>
            <Header activePage="reports" menuActive={menuActive} setMenuActive={setMenuActive} />
            <div className="main-content">
                <div className={`content-wrapper`}>
                    <HeaderInfo title='Reports' description={
                        activePage.startsWith('Open Reports')
                            ? `You have ${reportCount?.OPEN || 0} open reports.`
                            : activePage.startsWith('Closed Reports')
                                ? `You have ${closeReportCount || 0} close reports.`
                                : ''
                    } />
                    <PageTabs
                        tabs={ProfileTabs}
                        activePage={activePage}
                        setActivePage={(tabName) => {
                            setActivePage(tabName);
                            setFilterReport({
                                userType: [],
                                reportReason: [],
                                reportStatus: [],
                            })
                            setSearchQuery('')
                        }}
                    />
                    <div className="reportContent">
                        <Routes basename="/reports">
                            <Route path="/" element={getReportComponent(OpenReports)} ></Route>
                            <Route path="/openreports" element={getReportComponent(OpenReports)} ></Route>
                            <Route path="/closereports" element={getReportComponent(CloseReport)}></Route>
                        </Routes>
                    </div>
                </div>
                <Footer menuActive={menuActive} />
            </div>
            {
                showNotification &&
                <Notification showNotification={showNotification} notificationMessage={notificationMessage} setShowNotification={setShowNotification} />
            }
            <EditReport
                setEditReport={setEditReport}
                ref={userEditRef}
                selectedReport={selectedReport}
                isEditReport={isEditReport}
                handleActionUser={handleSuspendUser}
                isSuspendLoading={isSuspendLoading}
                setSelectedReport={setSelectedReport}
                handleIgnoreUSer={handleIgnoreUser}
                isIgnoreLoading={isIgnoreLoading}
            />
            {
                reportSuspendPopup &&
                <SingleSuspendPopup
                    ref={suspendVcRef}
                    isOpen={reportSuspendPopup}
                    onClose={() => setReportSuspendPopup(false)}
                    title={`Are you sure you want to delete ${selectedReport?.reportedName}?`}
                    description="Suspending this report means they will be permanently suspended from using Ivycorn."
                    setShowNotification={setShowNotification}
                    setNotificationMessage={setNotificationMessage}
                    setToggleSelect={setToggleSelect}
                    toggleSelect={toggleSelect}
                    handleActionUser={handleReportSuspend}
                    reason={reason}
                    setReason={setReason}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    isSuspendLoading={isSuspendLoading}
                />
            }
            {
                multipleReportSuspendPopup &&
                <MultipleSuspendPopup
                    ref={suspendMultipleVcRef}
                    isOpen={multipleReportSuspendPopup}
                    onClose={() => setMultipleReportSuspendPopup(false)}
                    setMultipleSuspendPopup={setMultipleReportSuspendPopup}
                    title={`Are you sure you want to delete these ${selectedRows?.length} report?`}
                    description="Deleting these delete means they will be permanently deleted from using Ivycorn."
                    setShowNotification={setShowNotification}
                    setNotificationMessage={setNotificationMessage}
                    setToggleSelect={setToggleSelect}
                    handleActionUser={handleReportSuspend}
                    reason={reason}
                    setReason={setReason}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    isSuspendLoading={isSuspendLoading}
                />
            }
        </div>
    )
}

export default Reports
